// contacts-update-banner — a dismissible, auto-dismissing top banner shown on
// My Contacts when a live refetch (or "Sync all") discovers new/updated
// contacts. Styling/behaviour mirror approvals-banner: inline-styled bar at the
// top of the contacts main, auto-dismiss ~9s, manually dismissible, and
// STACK-SAFE (a new summary replaces the existing bar + resets the timer rather
// than stacking multiple bars). The summary-copy generator is PURE + exported
// for unit tests.
import { h, icon } from './dom.js';
import type { ContactsDiff } from '../lib/contacts-diff.js';

const AUTO_DISMISS_MS = 9000;

// joinNames renders 1/2/3 names with proper comma/"and": "A", "A and B",
// "A, B and C".
function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names[2]}`;
}

// contactsSummaryText builds the banner copy from a diff. PURE — no DOM.
// New:     <=3 -> "added Alice, Bob and Carol"; else "<N> new contacts added".
// Updated: <=3 -> "Alice and Bob updated their info"; else "<N> contacts updated their info".
// Both present -> joined with " · ". Empty diff -> "".
export function contactsSummaryText(d: ContactsDiff): string {
  const parts: string[] = [];

  if (d.addedCount > 0) {
    if (d.addedCount <= 3) {
      parts.push(`added ${joinNames(d.addedNames)}`);
    } else {
      parts.push(`${d.addedCount} new contacts added`);
    }
  }

  if (d.updatedCount > 0) {
    if (d.updatedCount <= 3) {
      parts.push(`${joinNames(d.updatedNames)} updated their info`);
    } else {
      parts.push(`${d.updatedCount} contacts updated their info`);
    }
  }

  return parts.join(' · ');
}

let bannerEl: HTMLElement | null = null;
let dismissTimer: ReturnType<typeof setTimeout> | null = null;

function dismiss(): void {
  if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
  if (bannerEl?.parentElement) bannerEl.parentElement.removeChild(bannerEl);
  bannerEl = null;
}

// mountContactsUpdateBanner shows (or refreshes) the summary bar at the top of
// `container`. Stack-safe: an existing bar is replaced and the timer reset.
export function mountContactsUpdateBanner(container: HTMLElement, text: string): void {
  if (!text) return;
  // Replace any existing banner (stack-safe) and reset the auto-dismiss timer.
  dismiss();
  bannerEl = h('div', {
    class: 'banner contacts-update-banner',
    style: 'display:flex;align-items:center;gap:12px;padding:10px 16px;margin:0 0 12px;'
      + 'background:var(--accent-soft,#eef4ff);border:1px solid var(--accent,#3b82f6);'
      + 'border-radius:12px;pointer-events:auto',
    role: 'status',
  },
    icon('<path d="M9 12l2 2 4-4"/><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>', 20),
    h('div', { style: 'flex:1;min-width:0;font-weight:700' }, text),
    h('button', { class: 'btn btn-sm', type: 'button', 'aria-label': 'Dismiss',
      onclick: () => dismiss() }, 'Dismiss'),
  );
  container.insertBefore(bannerEl, container.firstChild);
  dismissTimer = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
}
